import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Building2, Edit, Trash2, Power, ArrowLeft, MapPin, Phone, Mail, Hash, Users, Lock, FileSignature, Upload, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import painelApi from '@/lib/painelApi';
import { toast } from 'sonner';

const REGIMES = [
  { value: 'mei', label: 'MEI' },
  { value: 'simples', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' },
];

const ESTADOS_BR = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const formaPadrao = () => ({
  nome: '', razao_social: '', nome_fantasia: '', cnpj: '', inscricao_estadual: '', inscricao_municipal: '',
  regime_tributario: 'simples', telefone: '', email: '',
  endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', cep: '',
  modulos: ['oficina'], ambiente_nfe: 'homologacao', focus_nfe_token: '', isento_ie: false,
});

function PainelEmpresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isVinculoOpen, setIsVinculoOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(formaPadrao());
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [empresasVinculadas, setEmpresasVinculadas] = useState([]);
  const [cepLoading, setCepLoading] = useState(false);
  const [certificadoInfo, setCertificadoInfo] = useState(null);
  const [nfeStatus, setNfeStatus] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [certSenha, setCertSenha] = useState('');
  const [uploadingCert, setUploadingCert] = useState(false);
  const [validandoToken, setValidandoToken] = useState(false);

  useEffect(() => {
    loadEmpresas();
    loadUsuarios();
  }, []);

  const loadEmpresas = async () => {
    try {
      const { data } = await painelApi.get('/painel/empresas');
      setEmpresas(data);
    } catch {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const loadUsuarios = async () => {
    try {
      const { data } = await painelApi.get('/painel/usuarios/oficina');
      setUsuarios(data);
    } catch {
      console.warn('Erro ao carregar usuários');
    }
  };

  const buscarCep = async (cep) => {
    const c = (cep || '').replace(/\D/g, '');
    if (c.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
      const d = await r.json();
      if (!d.erro) {
        setFormData((prev) => ({
          ...prev,
          endereco: d.logradouro || prev.endereco,
          bairro: d.bairro || prev.bairro,
          cidade: d.localidade || prev.cidade,
          estado: d.uf || prev.estado,
        }));
        toast.success('CEP encontrado');
      }
    } catch (err) {
      // CEP service can fail silently — common when offline or rate-limited
      console.warn('[ViaCEP]', err?.message);
    } finally {
      setCepLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setFormData(formaPadrao());
    setCertificadoInfo(null);
    setNfeStatus(null);
    setCertFile(null);
    setCertSenha('');
    setIsDialogOpen(true);
  };

  const openEdit = async (empresa) => {
    setEditing(empresa);
    setFormData({ ...formaPadrao(), ...empresa, focus_nfe_token: '' });  // nunca pré-preenche token (sensível)
    setCertFile(null);
    setCertSenha('');
    setIsDialogOpen(true);
    // carregar info do certificado e status NFe em paralelo
    try {
      const [certRes, nfeRes] = await Promise.all([
        painelApi.get(`/painel/empresas/${empresa.id}/certificado/info`),
        painelApi.get(`/painel/empresas/${empresa.id}/nfe-status`),
      ]);
      setCertificadoInfo(certRes.data);
      setNfeStatus(nfeRes.data);
    } catch {
      setCertificadoInfo({ has_certificado: false });
      setNfeStatus(null);
    }
  };

  const recarregarCertInfo = async () => {
    if (!editing) return;
    try {
      const [certRes, nfeRes] = await Promise.all([
        painelApi.get(`/painel/empresas/${editing.id}/certificado/info`),
        painelApi.get(`/painel/empresas/${editing.id}/nfe-status`),
      ]);
      setCertificadoInfo(certRes.data);
      setNfeStatus(nfeRes.data);
    } catch (err) {
      // Empresa não tem certificado/status carregados ainda — comum em criação
      console.warn('[recarregarCertInfo]', err?.response?.status, err?.message);
    }
  };

  const uploadCertificado = async () => {
    if (!editing) {
      toast.error('Salve a empresa antes de enviar o certificado');
      return;
    }
    if (!certFile) {
      toast.error('Selecione um arquivo .pfx');
      return;
    }
    if (!certSenha) {
      toast.error('Informe a senha do certificado');
      return;
    }
    setUploadingCert(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', certFile);
      fd.append('senha', certSenha);
      const { data } = await painelApi.post(`/painel/empresas/${editing.id}/certificado`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Certificado enviado: ${data.cn || 'OK'}`);
      setCertFile(null);
      setCertSenha('');
      await recarregarCertInfo();
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar certificado');
    } finally {
      setUploadingCert(false);
    }
  };

  const removerCertificado = async () => {
    if (!editing) return;
    if (!window.confirm('Remover certificado digital desta empresa?')) return;
    try {
      await painelApi.delete(`/painel/empresas/${editing.id}/certificado`);
      toast.success('Certificado removido');
      await recarregarCertInfo();
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao remover');
    }
  };

  const validarToken = async () => {
    const tokenDigitado = (formData.focus_nfe_token || '').trim();
    // Se digitou um token novo (e não é "REMOVER"), valida-o.
    // Senão, valida o token salvo da empresa em edição.
    const usaSalvo = !tokenDigitado || tokenDigitado === 'REMOVER';
    if (usaSalvo && !editing?.tem_token_proprio) {
      toast.error('Digite um token para validar ou salve um token na empresa antes');
      return;
    }
    setValidandoToken(true);
    try {
      const body = usaSalvo
        ? { empresa_id: editing.id, ambiente: formData.ambiente_nfe }
        : { token: tokenDigitado, ambiente: formData.ambiente_nfe };
      const { data } = await painelApi.post('/nfe/validar-token', body);
      if (data.valido) {
        toast.success(`Token válido em ${data.ambiente || formData.ambiente_nfe}`);
      } else {
        toast.error(`Token inválido: ${data.erro || 'falha desconhecida'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao validar token');
    } finally {
      setValidandoToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!formData.modulos || formData.modulos.length === 0) {
      toast.error('Selecione pelo menos um módulo');
      return;
    }
    try {
      const payload = { ...formData };
      // Tratar token Focus NFe: se vazio → não envia (mantém atual);
      // "REMOVER" (maiúsculo) → envia string vazia (limpa token próprio, usa global)
      if (payload.focus_nfe_token === '' || payload.focus_nfe_token === undefined || payload.focus_nfe_token === null) {
        delete payload.focus_nfe_token;
      } else if (payload.focus_nfe_token === 'REMOVER') {
        payload.focus_nfe_token = '';
      }
      if (editing) {
        await painelApi.put(`/painel/empresas/${editing.id}`, payload);
        toast.success('Empresa atualizada');
      } else {
        await painelApi.post('/painel/empresas', payload);
        toast.success('Empresa criada');
      }
      setIsDialogOpen(false);
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar empresa');
    }
  };

  const handleDelete = async (empresa) => {
    if (empresa.is_default_oficina) {
      toast.error('Empresa Padrão não pode ser excluída');
      return;
    }
    if (!window.confirm(`Excluir empresa "${empresa.nome}"? Essa ação é permanente.`)) return;
    try {
      await painelApi.delete(`/painel/empresas/${empresa.id}`);
      toast.success('Empresa excluída');
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleToggleAtiva = async (empresa) => {
    try {
      const { data } = await painelApi.post(`/painel/empresas/${empresa.id}/toggle-ativa`);
      toast.success(`Empresa ${data.ativa ? 'ativada' : 'desativada'}`);
      loadEmpresas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const openVinculo = (usuario) => {
    setUsuarioSelecionado(usuario);
    setEmpresasVinculadas(usuario.empresa_ids || []);
    setIsVinculoOpen(true);
  };

  const toggleVinculo = (empresaId) => {
    setEmpresasVinculadas((prev) =>
      prev.includes(empresaId) ? prev.filter((e) => e !== empresaId) : [...prev, empresaId]
    );
  };

  const salvarVinculos = async () => {
    if (!usuarioSelecionado) return;
    try {
      await painelApi.put(`/painel/usuarios/oficina/${usuarioSelecionado.id}/empresas`, {
        empresa_ids: empresasVinculadas,
      });
      toast.success('Vínculos atualizados');
      setIsVinculoOpen(false);
      loadUsuarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar vínculos');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-obsidian flex items-center justify-center text-slate-400">Carregando...</div>;
  }

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="painel-empresas-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-carbon/50 backdrop-blur-sm sticky top-0 z-30">
        <div className="container mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/painel')} className="text-slate-300 hover:text-white" data-testid="back-painel-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Painel
            </Button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold uppercase tracking-tight">Empresas</h1>
              <p className="text-xs text-slate-500">Gestão multi-tenant — {empresas.length} empresa(s)</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="add-empresa-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Empresa
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-8 space-y-10">
        {/* Lista de Empresas */}
        <section>
          <h2 className="text-sm uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Empresas Cadastradas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {empresas.length === 0 ? (
              <div className="col-span-full bg-carbon border border-white/5 rounded-md p-12 text-center">
                <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">Nenhuma empresa cadastrada</p>
              </div>
            ) : (
              empresas.map((empresa) => (
                <motion.div
                  key={empresa.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-carbon border rounded-md p-5 transition-colors ${
                    empresa.ativa === false ? 'border-white/5 opacity-60' : 'border-white/5 hover:border-white/20'
                  }`}
                  data-testid={`empresa-card-${empresa.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-electric-blue/10 text-electric-blue flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-white truncate flex items-center gap-2">
                          {empresa.nome}
                          {empresa.is_default_oficina && <Lock className="w-3 h-3 text-amber-400" title="Empresa padrão" />}
                        </h3>
                        <p className="text-xs text-slate-500 truncate">{empresa.razao_social || empresa.cnpj || '—'}</p>
                      </div>
                    </div>
                    {empresa.ativa === false && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 text-red-400 uppercase">Inativa</span>
                    )}
                  </div>

                  <div className="space-y-1.5 text-xs text-slate-400 mb-4">
                    {empresa.cnpj && <div className="flex items-center gap-2"><Hash className="w-3 h-3 text-slate-500" />{empresa.cnpj}</div>}
                    {(empresa.cidade || empresa.estado) && (
                      <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-slate-500" />{[empresa.cidade, empresa.estado].filter(Boolean).join(' / ')}</div>
                    )}
                    {empresa.telefone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-slate-500" />{empresa.telefone}</div>}
                    {empresa.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3 h-3 text-slate-500" />{empresa.email}</div>}
                    <div className="flex items-center gap-2 pt-1">
                      <Users className="w-3 h-3 text-slate-500" />
                      <span>{empresa.total_usuarios || 0} usuário(s)</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-electric-blue">{(empresa.modulos || []).join(', ') || '—'}</span>
                    </div>
                    {empresa.certificado_filename && (
                      <div className="flex items-center gap-2 pt-1">
                        <FileSignature className="w-3 h-3 text-electric-blue" />
                        <span className="text-electric-blue">Certificado A1</span>
                        <span className="text-slate-600">·</span>
                        <span className={`text-[10px] uppercase ${
                          (empresa.ambiente_nfe || 'homologacao') === 'producao'
                            ? 'text-red-400' : 'text-amber-400'
                        }`}>
                          {(empresa.ambiente_nfe || 'homologacao') === 'producao' ? 'Produção' : 'Homologação'}
                        </span>
                        {empresa.tem_token_proprio && (
                          <>
                            <span className="text-slate-600">·</span>
                            <span className="text-[10px] uppercase text-emerald-400">Token próprio</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-1 pt-3 border-t border-white/5">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(empresa)} className="text-electric-blue hover:bg-electric-blue/10" data-testid={`edit-empresa-${empresa.id}`}>
                      <Edit className="w-3.5 h-3.5 mr-1" /> Editar
                    </Button>
                    {!empresa.is_default_oficina && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleAtiva(empresa)}
                          className={empresa.ativa === false ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-amber-400 hover:bg-amber-500/10'}
                          data-testid={`toggle-empresa-${empresa.id}`}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(empresa)} className="text-red-400 hover:bg-red-500/10" data-testid={`delete-empresa-${empresa.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </section>

        {/* Vínculos Usuário-Empresa */}
        <section>
          <h2 className="text-sm uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" /> Vínculo Usuário ↔ Empresa (Oficina)
          </h2>
          <div className="bg-carbon border border-white/5 rounded-md overflow-hidden">
            <div className="divide-y divide-white/5">
              {usuarios.length === 0 ? (
                <div className="p-6 text-slate-500 text-sm text-center">Nenhum usuário cadastrado</div>
              ) : (
                usuarios.map((u) => (
                  <div key={u.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 gap-3" data-testid={`usuario-row-${u.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{u.nome}</p>
                      <p className="text-xs text-slate-500">@{u.username} · {u.role === 'admin' ? 'Admin' : 'Usuário'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-slate-400 max-w-[300px] truncate">
                        {(u.empresas_nomes || []).length > 0 ? u.empresas_nomes.join(', ') : <span className="text-amber-400">Sem empresas</span>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => openVinculo(u)} className="text-electric-blue hover:bg-electric-blue/10" data-testid={`vincular-usuario-${u.id}`}>
                        Vincular
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Dialog Empresa */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">{editing ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
            <DialogDescription className="text-slate-500">Dados cadastrais e fiscais da empresa</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nome / Apelido *</Label>
                <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-nome" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Razão Social</Label>
                <Input value={formData.razao_social || ''} onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-razao" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nome Fantasia</Label>
                <Input value={formData.nome_fantasia || ''} onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-fantasia" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">CNPJ</Label>
                <Input value={formData.cnpj || ''} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-cnpj" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center justify-between">
                  <span>Inscrição Estadual</span>
                  <label className="flex items-center gap-1.5 normal-case tracking-normal text-[11px] text-slate-300 cursor-pointer">
                    <Checkbox
                      checked={!!formData.isento_ie}
                      onCheckedChange={(v) => setFormData({ ...formData, isento_ie: !!v, inscricao_estadual: v ? '' : formData.inscricao_estadual })}
                      data-testid="emp-isento-ie"
                    />
                    Isento de IE
                  </label>
                </Label>
                <Input
                  value={formData.isento_ie ? 'ISENTO' : (formData.inscricao_estadual || '')}
                  onChange={(e) => setFormData({ ...formData, inscricao_estadual: e.target.value })}
                  disabled={!!formData.isento_ie}
                  placeholder={formData.isento_ie ? 'Empresa isenta de Inscrição Estadual' : 'Digite a IE'}
                  className="bg-obsidian border-white/10 text-white disabled:opacity-60 disabled:text-amber-300"
                  data-testid="emp-ie"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Regime Tributário</Label>
                <Select value={formData.regime_tributario || 'simples'} onValueChange={(v) => setFormData({ ...formData, regime_tributario: v })}>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {REGIMES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Telefone</Label>
                <Input value={formData.telefone || ''} onChange={(e) => setFormData({ ...formData, telefone: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-telefone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">E-mail</Label>
                <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-obsidian border-white/10 text-white" data-testid="emp-email" />
              </div>
            </div>

            <div className="border-t border-white/5 pt-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Endereço</p>
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                <div className="col-span-2 sm:col-span-2">
                  <Label className="text-xs text-slate-400">CEP</Label>
                  <Input
                    value={formData.cep || ''}
                    onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                    onBlur={(e) => buscarCep(e.target.value)}
                    placeholder="00000-000"
                    className="bg-obsidian border-white/10 text-white"
                    data-testid="emp-cep"
                  />
                  {cepLoading && <p className="text-[10px] text-electric-blue mt-1">Buscando...</p>}
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs text-slate-400">Logradouro</Label>
                  <Input value={formData.endereco || ''} onChange={(e) => setFormData({ ...formData, endereco: e.target.value })} className="bg-obsidian border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Nº</Label>
                  <Input value={formData.numero || ''} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} className="bg-obsidian border-white/10 text-white" />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs text-slate-400">Bairro</Label>
                  <Input value={formData.bairro || ''} onChange={(e) => setFormData({ ...formData, bairro: e.target.value })} className="bg-obsidian border-white/10 text-white" />
                </div>
                <div className="col-span-2 sm:col-span-2">
                  <Label className="text-xs text-slate-400">Cidade</Label>
                  <Input value={formData.cidade || ''} onChange={(e) => setFormData({ ...formData, cidade: e.target.value })} className="bg-obsidian border-white/10 text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">UF</Label>
                  <Select value={formData.estado || ''} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                    <SelectTrigger className="bg-obsidian border-white/10 text-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-carbon border-white/10 text-white max-h-60">
                      {ESTADOS_BR.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-t border-white/5 pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Módulos</Label>
              <div className="flex gap-3 flex-wrap">
                {['oficina', 'financas'].map((m) => (
                  <label key={m} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300 hover:text-white">
                    <Checkbox
                      checked={formData.modulos.includes(m)}
                      onCheckedChange={(checked) => {
                        setFormData({
                          ...formData,
                          modulos: checked ? [...formData.modulos, m] : formData.modulos.filter((x) => x !== m),
                        });
                      }}
                      data-testid={`emp-modulo-${m}`}
                    />
                    {m === 'oficina' ? 'Oficina Mecânica' : 'Financeiro MEI/ME'}
                  </label>
                ))}
              </div>
            </div>

            {/* Seção Fiscal / NFe — apenas em edição */}
            {editing && (
              <div className="border-t border-white/5 pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <FileSignature className="w-3.5 h-3.5" /> Configuração Fiscal (NFe)
                  </Label>
                  {nfeStatus && (
                    nfeStatus.apta ? (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 uppercase font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Apta a emitir
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 uppercase font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {nfeStatus.motivos?.length || 0} pendência(s)
                      </span>
                    )
                  )}
                </div>

                {nfeStatus && !nfeStatus.apta && nfeStatus.motivos && nfeStatus.motivos.length > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2 space-y-1">
                    {nfeStatus.motivos.map((m, i) => (
                      <p key={`motivo-${i}-${m.slice(0, 20)}`} className="text-[11px] text-amber-300/90 flex items-start gap-1.5">
                        <span className="text-amber-500">•</span>
                        <span>{m}</span>
                      </p>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Ambiente NFe</Label>
                    <div className="flex items-center gap-3 bg-obsidian border border-white/10 rounded-md px-3 py-2 mt-1">
                      <Switch
                        checked={formData.ambiente_nfe === 'producao'}
                        onCheckedChange={(v) => setFormData({ ...formData, ambiente_nfe: v ? 'producao' : 'homologacao' })}
                        data-testid="emp-ambiente-nfe"
                      />
                      <span className={`text-sm font-medium ${formData.ambiente_nfe === 'producao' ? 'text-red-400' : 'text-amber-300'}`}>
                        {formData.ambiente_nfe === 'producao' ? 'PRODUÇÃO (notas reais)' : 'Homologação (testes)'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 flex items-center justify-between">
                      <span>Token Focus NFe (opcional)</span>
                      {editing?.tem_token_proprio && (
                        <span className="text-[10px] uppercase text-emerald-400 font-medium">Token próprio</span>
                      )}
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="password"
                        value={formData.focus_nfe_token || ''}
                        onChange={(e) => setFormData({ ...formData, focus_nfe_token: e.target.value })}
                        placeholder={editing?.tem_token_proprio ? '••••••••  (deixe vazio para manter)' : 'Cole o token Focus NFe aqui'}
                        className="bg-obsidian border-white/10 text-white text-sm flex-1"
                        data-testid="emp-focus-token"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={validarToken}
                        disabled={validandoToken || (!formData.focus_nfe_token?.trim() && !editing?.tem_token_proprio)}
                        className="border-electric-blue/40 text-electric-blue hover:bg-electric-blue/10 whitespace-nowrap"
                        data-testid="validar-token-btn"
                      >
                        {validandoToken ? 'Validando...' : 'Validar Token'}
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {editing?.tem_token_proprio
                        ? 'Para remover o token próprio e usar o global, digite "REMOVER". Use "Validar Token" para testar conectividade.'
                        : 'Se vazio, usa o token global do sistema (homologação). Digite um token e clique "Validar Token" para testar.'}
                    </p>
                  </div>
                </div>

                {/* Status do certificado */}
                {certificadoInfo?.has_certificado ? (
                  <div className="bg-obsidian/50 border border-white/5 rounded p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileSignature className="w-4 h-4 text-electric-blue flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">{certificadoInfo.cn || certificadoInfo.filename}</p>
                          <p className="text-[10px] text-slate-500">{certificadoInfo.filename}</p>
                        </div>
                      </div>
                      <Button type="button" size="sm" variant="ghost" onClick={removerCertificado} className="text-red-400 hover:bg-red-500/10 flex-shrink-0" data-testid="remover-cert-btn">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div>
                        <span className="text-slate-500">Validade:</span>{' '}
                        <span className={certificadoInfo.vencido ? 'text-red-400' : 'text-slate-300'}>
                          {certificadoInfo.valido_ate ? new Date(certificadoInfo.valido_ate).toLocaleDateString('pt-BR') : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Restantes:</span>{' '}
                        <span className={
                          certificadoInfo.dias_restantes === null ? 'text-slate-300' :
                          certificadoInfo.dias_restantes < 0 ? 'text-red-400' :
                          certificadoInfo.dias_restantes < 30 ? 'text-amber-400' : 'text-emerald-400'
                        }>
                          {certificadoInfo.dias_restantes === null ? '—' :
                           certificadoInfo.dias_restantes < 0 ? 'Vencido' :
                           `${certificadoInfo.dias_restantes} dia(s)`}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-obsidian/50 border border-dashed border-white/10 rounded p-3 space-y-2">
                    <p className="text-xs text-slate-400 mb-1">Envie o certificado digital A1 (.pfx ou .p12)</p>
                    <Input
                      type="file"
                      accept=".pfx,.p12"
                      onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                      className="bg-obsidian border-white/10 text-white text-xs file:bg-electric-blue/20 file:text-electric-blue file:border-0 file:px-3 file:py-1 file:rounded file:text-xs file:mr-3 file:cursor-pointer"
                      data-testid="cert-file-input"
                    />
                    <Input
                      type="password"
                      placeholder="Senha do certificado"
                      value={certSenha}
                      onChange={(e) => setCertSenha(e.target.value)}
                      className="bg-obsidian border-white/10 text-white text-sm"
                      data-testid="cert-senha-input"
                    />
                    <Button
                      type="button"
                      onClick={uploadCertificado}
                      disabled={uploadingCert || !certFile || !certSenha}
                      className="w-full bg-electric-blue/20 text-electric-blue hover:bg-electric-blue/30 disabled:opacity-50"
                      data-testid="upload-cert-btn"
                    >
                      <Upload className="w-3.5 h-3.5 mr-2" />
                      {uploadingCert ? 'Enviando...' : 'Enviar Certificado'}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
              <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-empresa-btn">
                {editing ? 'Salvar Alterações' : 'Criar Empresa'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Vínculos */}
      <Dialog open={isVinculoOpen} onOpenChange={setIsVinculoOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">Vincular a Empresas</DialogTitle>
            <DialogDescription className="text-slate-500">
              {usuarioSelecionado?.nome} (@{usuarioSelecionado?.username})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {empresas.filter((e) => e.modulos?.includes('oficina')).map((e) => (
              <label
                key={e.id}
                className="flex items-center gap-3 p-2 rounded hover:bg-white/5 cursor-pointer"
                data-testid={`vinculo-checkbox-${e.id}`}
              >
                <Checkbox
                  checked={empresasVinculadas.includes(e.id)}
                  onCheckedChange={() => toggleVinculo(e.id)}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{e.nome}</p>
                  {e.cnpj && <p className="text-xs text-slate-500 truncate">{e.cnpj}</p>}
                </div>
                {e.ativa === false && <span className="text-[10px] text-red-400">Inativa</span>}
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
            <Button variant="ghost" onClick={() => setIsVinculoOpen(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={salvarVinculos} className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="save-vinculos-btn">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PainelEmpresas;
